import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Grid, Box, List, ListItemButton, ListItemText,
  Chip, Button, CircularProgress, Tooltip, alpha, Divider, Card, CardContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ErrorOutline, AccessTime, PeopleOutline, AttachMoney, CalendarMonth, Launch, GolfCourse, InfoOutlined
} from '@mui/icons-material';
import { isValid, parseISO, format } from 'date-fns';

// Assuming this helper exists and formats time strings appropriately (e.g., "HH:mm:ss" to "h:mm a")
import { formatTimeForDisplay } from '../utils/helpers';

// --- Helper component for individual Tee Time Card ---
// No changes needed in TeeTimeCard itself
const TeeTimeCard = ({ teeTime, theme }) => (
  <Card
    variant="outlined"
    sx={{
      // mb: 1.5, // Margin bottom might not be needed with Grid spacing, adjust if necessary
      height: '100%', // Ensure cards fill grid item height potentially
      borderRadius: 3,
      backgroundColor: alpha(theme.palette.background.paper, 0.9),
      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
      transition: 'box-shadow 0.2s ease-in-out',
      '&:hover': {
        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
        borderColor: alpha(theme.palette.primary.light, 0.5)
      }
    }}
  >
    <CardContent sx={{ p: '12px !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      {/* Time */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: '100px', mr: 1 }}>
        <AccessTime sx={{ mr: 0.8, color: theme.palette.primary.main, fontSize: '1.3rem' }} />
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: theme.palette.primary.dark, fontSize: '1.1rem' }}>
          {formatTimeForDisplay ? formatTimeForDisplay(teeTime.time) : teeTime.time}
        </Typography>
      </Box>

      {/* Details (Slots & Price) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', justifyContent: 'flex-start', mr: 1 }}>
        <Chip
          icon={<PeopleOutline sx={{ fontSize: '1rem', ml: '4px' }} />}
          label={`${teeTime.available_slots} slots`}
          size="small"
          sx={{
            height: 'auto', lineHeight: 1.4, p: '2px 6px',
            backgroundColor: alpha(theme.palette.info.light, 0.15),
            color: theme.palette.info.dark, fontWeight: 'medium', fontSize: '0.75rem',
          }}
        />
        {teeTime.price != null && (
          <Chip
            icon={<AttachMoney sx={{ fontSize: '1rem', ml: '4px' }} />}
            label={`$${Number(teeTime.price).toFixed(2)}`}
            size="small"
            sx={{
              height: 'auto', lineHeight: 1.4, p: '2px 6px',
              backgroundColor: alpha(theme.palette.success.light, 0.15),
              color: theme.palette.success.dark, fontWeight: 'medium', fontSize: '0.75rem',
            }}
          />
        )}
      </Box>

      {/* Booking Button */}
      {teeTime.booking_link && (
        <Button
          variant="contained"
          size="small"
          color="primary"
          href={teeTime.booking_link}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<Launch sx={{ fontSize: '0.9rem' }} />}
          sx={{
            borderRadius: 8, fontWeight: 'bold', p: '2px 10px', fontSize: '0.75rem',
            ml: { xs: 'auto', sm: 1 },
            mt: { xs: 1, sm: 0 },
            alignSelf: 'center'
          }}
        >
          Book
        </Button>
      )}
    </CardContent>
  </Card>
);


// --- Main SearchResults Component ---
const SearchResults = ({ loadingSearch, results, searchId, selectedCourses = [] }) => {
  const theme = useTheme();
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  // --- Memoized variables and useEffect hooks remain the same ---
  const coursesInCurrentResults = useMemo(() => {
    if (!results || !searchId) return [];
    const resultKeys = Object.keys(results);
    return [...selectedCourses]
      .filter(course => resultKeys.includes(String(course.id)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [results, searchId, selectedCourses]);

  const selectedCourseData = useMemo(() => {
      if (!selectedCourseId || !results || !results[selectedCourseId]) return null;
      return results[selectedCourseId];
  }, [selectedCourseId, results]);

  useEffect(() => {
    if (coursesInCurrentResults.length > 0) {
        const currentSelectionStillValid = selectedCourseId && coursesInCurrentResults.some(c => c.id === selectedCourseId);
        if (!currentSelectionStillValid) {
            const firstWithTimes = coursesInCurrentResults.find(course => results[course.id]?.tee_times?.length > 0)?.id;
            setSelectedCourseId(firstWithTimes || coursesInCurrentResults[0].id);
        }
    } else if (!loadingSearch) {
         setSelectedCourseId(null);
    }
  }, [coursesInCurrentResults, results, selectedCourseId, loadingSearch]);

  // --- getCourseStatusIndicator remains the same ---
  const getCourseStatusIndicator = (course) => {
      const courseResult = results ? results[course.id] : null;
      if (!courseResult) return null;
      if (courseResult.error_message) {
          return ( <Tooltip title={courseResult.error_message}><ErrorOutline color="error" sx={{ ml: 1, fontSize: '1.1rem', verticalAlign: 'middle' }} /></Tooltip> );
      }
      if (courseResult.tee_times?.length > 0) {
          return ( <Chip label={courseResult.tee_times.length} size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem', fontWeight: 'bold', bgcolor: alpha(theme.palette.success.main, 0.15), color: theme.palette.success.dark }} /> );
      }
      if (courseResult.tee_times?.length === 0 && !courseResult.error_message) {
          return ( <Chip label="0" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.7rem', borderColor: alpha(theme.palette.text.secondary, 0.3), color: theme.palette.text.secondary }} /> );
      }
      return null;
  };

  const searchCompleted = !loadingSearch && !!searchId && !!results;
  const displayableResultsExist = useMemo(() => {
      if (!results) return false;
      return coursesInCurrentResults.some(course => results[course.id]?.tee_times?.length > 0);
  }, [coursesInCurrentResults, results]);

  return (
    <Paper
      // --- Outer Paper styles remain the same ---
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 }, mt: 3, mb: 4, borderRadius: 4,
        backgroundColor: alpha(theme.palette.background.default, 0.85),
        backdropFilter: 'blur(5px)', maxWidth: '1200px', margin: '0 auto',
      }}
    >
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: `2px solid ${alpha(theme.palette.primary.light, 0.5)}` }}>
        <Typography variant="h6" component="h2" sx={{ color: theme.palette.primary.dark, fontWeight: 'bold', fontSize: '1.3rem' }}>
          <GolfCourse sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1.6rem' }} /> Available Tee Times
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
           {loadingSearch && ( <CircularProgress size={20} sx={{ mr: 1 }}/> )}
           {searchId && (
             <Tooltip title={`Results for Search ID: ${searchId}`}>
                 <Typography component="span" variant="caption" color="text.secondary"> ID: {searchId.substring(0, 8)}...</Typography>
             </Tooltip>
           )}
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Left Column: Course List */}
        <Grid item xs={12} md={4}>
           {/* --- Left Panel content remains the same --- */}
           <Paper elevation={1} sx={{ borderRadius: 3, p: 1.5, height: '100%', border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, backgroundColor: alpha(theme.palette.background.paper, 0.9) }}>
             <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, px: 1, fontSize: '1rem' }}> Courses in Search </Typography>
             <List dense sx={{ p: 0 }}>
               {coursesInCurrentResults.map((course) => (
                 <ListItemButton key={course.id} selected={selectedCourseId === course.id} onClick={() => setSelectedCourseId(course.id)} sx={{ borderRadius: 2, mb: 0.5, py: 0.8, '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.1), '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.15), }, }, '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04), } }}>
                   <ListItemText primary={ <Typography variant="body2" sx={{ fontWeight: selectedCourseId === course.id ? 'bold' : 'medium' }}>{course.name}</Typography> } secondary={ <Typography variant="caption" color="text.secondary">{course.club_name}</Typography> } sx={{ m: 0 }} />
                   {results && getCourseStatusIndicator(course)}
                 </ListItemButton>
               ))}
               {searchCompleted && coursesInCurrentResults.length === 0 && selectedCourses.length > 0 && ( <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', mt: 1, border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`, borderRadius: 2 }}> <InfoOutlined fontSize="small" /> <Typography variant="body2" > Last search didn't include currently selected courses. Search again to update. </Typography> </Box> )}
               {selectedCourses.length === 0 && !loadingSearch && ( <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}> Select courses using the search bar above. </Typography> )}
               {loadingSearch && ( <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary'}}> <CircularProgress size={24} sx={{mb: 1}} /> <Typography variant="body2">Loading results...</Typography> </Box> )}
             </List>
           </Paper>
        </Grid>

        {/* Right Column: Tee Time Details */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ borderRadius: 3, p: { xs: 1.5, md: 2 }, minHeight: '300px', height: '100%', border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, backgroundColor: alpha(theme.palette.background.paper, 0.9), display: 'flex', flexDirection: 'column' }}>
            {/* --- Conditional Rendering for Right Panel --- */}

            {/* Case 1: No course selected */}
            {!selectedCourseId && !loadingSearch && coursesInCurrentResults.length > 0 && (
                 <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', color: theme.palette.text.secondary }}> <GolfCourse sx={{ fontSize: 40, mb: 1, color: theme.palette.primary.light }} /> <Typography>Select a course from the list</Typography> <Typography variant="body2">to view its results from this search.</Typography> </Box>
            )}

             {/* Case 2: Course selected, show its data */}
            {selectedCourseData ? (
                <>
                  {/* Header within the right panel */}
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.dark, fontSize: '1.1rem', mb: 0.5 }}> {selectedCourseData.course_name} </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}> {selectedCourseData.club_name} </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* Scrollable Area for Tee Times */}
                  <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: { xs: 'calc(100vh - 450px)', md: 'calc(100vh - 380px)' }, pr: 0.5, mr: -0.5,
                    '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { bgcolor: 'transparent' }, '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.primary.light, 0.4), borderRadius: '4px', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.6), } },
                  }}>
                    {/* Sub-case 2a: Error fetching times */}
                    {selectedCourseData.error_message ? (
                      <Tooltip title={selectedCourseData.error_message}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main', p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1) }}> <ErrorOutline sx={{ mr: 1, fontSize: '1.2rem' }} /> <Typography variant="body2" sx={{ fontWeight: 'medium' }}> An error occurred fetching times for this course. </Typography> </Box>
                      </Tooltip>
                    /* Sub-case 2b: Tee times found - RENDER GRID */
                    ) : selectedCourseData.tee_times.length > 0 ? (
                        // --- CHANGE: Wrap tee time cards in a Grid container ---
                        <Grid container spacing={2}>
                            {[...selectedCourseData.tee_times]
                            .sort((a, b) => a.time.localeCompare(b.time))
                            .map((teeTime, i) => (
                              // --- CHANGE: Each card is a Grid item ---
                              <Grid item xs={12} md={6} key={`${selectedCourseData.course_id}-${teeTime.time}-${i}`}>
                                <TeeTimeCard teeTime={teeTime} theme={theme} />
                              </Grid>
                            ))}
                        </Grid> // --- End of inner Grid container ---
                    /* Sub-case 2c: No tee times found */
                    ) : (
                       <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', color: theme.palette.text.secondary, minHeight: '150px' }}> <CalendarMonth sx={{ fontSize: 30, mb: 1 }} /> <Typography variant="body2">No available tee times found</Typography> <Typography variant="body2">in this search for this course.</Typography> </Box>
                    )}
                  </Box>

                  {/* Footer for Last Updated */}
                  <Box sx={{ pt: 1.5, mt: 'auto' }}>
                     <Divider sx={{ mb: 1 }} />
                     <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: 'right', fontStyle: 'italic' }}>
                        Updated: {selectedCourseData.last_updated && isValid(parseISO(selectedCourseData.last_updated)) ? format(parseISO(selectedCourseData.last_updated), 'Pp') : 'N/A'}
                      </Typography>
                  </Box>
                </>
            /* Case 3: Course selected, but no data */
             ) : selectedCourseId && !loadingSearch ? (
                 <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', color: theme.palette.text.secondary }}> <InfoOutlined sx={{ fontSize: 40, mb: 1 }} /> <Typography>No data available for this course</Typography> <Typography variant="body2">in the current search results (ID: {searchId?.substring(0,8)}...).</Typography> </Box>
             ) : null }

             {/* Case 4: Loading state placeholder */}
             {loadingSearch && (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}> <CircularProgress /> </Box>
             )}
          </Paper>
        </Grid>

        {/* Overall No Results Message */}
        {searchCompleted && coursesInCurrentResults.length > 0 && !displayableResultsExist && (
           <Grid item xs={12}>
             <Paper elevation={0} sx={{ p: 2, mt: 1, textAlign: 'center', borderRadius: 3, backgroundColor: alpha(theme.palette.warning.light, 0.15), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
               <Typography variant="body1" sx={{color: theme.palette.warning.dark, fontWeight:'medium'}}> Search completed. No available tee times found for the searched courses. </Typography>
               <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}> Try adjusting your date, time range, or searching different courses. </Typography>
             </Paper>
           </Grid>
         )}
      </Grid>
    </Paper>
  );
};

export default SearchResults;